import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useAuth } from '../auth/AuthContext'
import { stepUp } from '../auth/keycloak'
import { useCreateOrganization, useCreateRoom, useRooms } from '../api/rooms'
import { ApiError } from '../api/client'
import { errorMessage, formatUtc } from '../lib/format'
import type { RoomSummary } from '../api/types'

/** The landing page: the rooms the caller may enter, and the ways to make more. */
export function Rooms() {
  const { me } = useAuth()
  const rooms = useRooms()
  // A password-only session (ADR 0011) reaches only the rooms that allow it. Organization
  // and room-creation calls would answer a step-up challenge and send the browser straight
  // back to Keycloak, so they are not made until the person chooses to verify.
  const singleFactor = me?.multiFactor === false
  const isPlatformAdmin = !singleFactor && (me?.roles.includes('platform-admin') ?? false)
  const canOwnRooms =
    !singleFactor && (isPlatformAdmin || (me?.roles.includes('room-owner') ?? false))
  // Password-only with no room that allows it: nothing here is reachable, which is what a
  // newly enrolled user sees (Keycloak does not count an enrolment as a second factor,
  // ADR 0011 s3). Go straight to the step-up rather than show an empty list.
  const nothingReachable = singleFactor && rooms.data?.length === 0
  useEffect(() => {
    if (nothingReachable) stepUp()
  }, [nothingReachable])

  return (
    <section className="stack">
      <div className="row-between">
        <h1 className="h1">Rooms</h1>
      </div>

      {me?.firstLogin && (
        <div className="alert alert-info" role="status">
          Welcome, your account was just set up.
        </div>
      )}

      {singleFactor && <SingleFactorNotice />}

      {rooms.isPending && <p className="muted">Loading…</p>}
      {rooms.isError && (
        <div className="alert alert-error" role="alert">
          <p>{errorMessage(rooms.error)}</p>
        </div>
      )}
      {rooms.data && <RoomList rooms={rooms.data} />}

      {canOwnRooms && (me?.organizations.length ?? 0) > 0 && (
        <CreateRoomPanel
          organizations={me!.organizations.map((o) => ({ id: o.id, name: o.name }))}
        />
      )}

      {canOwnRooms && (me?.organizations.length ?? 0) === 0 && !isPlatformAdmin && (
        <p className="muted">
          You can own rooms, but you are not yet staff of any organization. A platform administrator
          or an organization owner has to add you.
        </p>
      )}

      {canOwnRooms && <OwnedOrganizations />}

      {isPlatformAdmin && <CreateOrganizationPanel />}
    </section>
  )
}

/** Shown to a session that signed in with a password alone (ADR 0011). */
export function SingleFactorNotice() {
  return (
    <div className="alert alert-info" role="status">
      <p>
        You signed in with your password only, so this list shows just the rooms that allow it.
        Other rooms, and anything you manage, need two-step verification.
      </p>
      <button type="button" className="btn btn-sm" onClick={() => void stepUp()}>
        Verify with your authenticator
      </button>
    </div>
  )
}

/**
 * The way to an organization's own page, for its Owners: the privacy and data use policy that
 * participants accept before they upload (ADR 0008). Hidden from everyone else, not guarded:
 * the API answers 403 to staff who are not Owners and 404 to anyone outside.
 */
function OwnedOrganizations() {
  const { me } = useAuth()
  const owned = me?.organizations.filter((o) => o.memberRole === 'Owner') ?? []
  if (owned.length === 0) return null

  return (
    <section className="panel">
      <h2 className="h2">Your organizations</h2>
      <ul className="plain-list">
        {owned.map((o) => (
          <li key={o.id}>
            <Link to={`/organizations/${o.id}`}>{o.name}</Link>{' '}
            <span className="small muted">
              privacy and data use policy, and who has accepted it
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}

export function RoomList({ rooms }: { rooms: RoomSummary[] }) {
  if (rooms.length === 0) {
    return <p className="muted">You are not in any room yet. A room owner has to add you.</p>
  }
  return (
    <ul className="card-list">
      {rooms.map((room) => (
        <li key={room.id} className="card">
          <Link to={`/rooms/${room.id}`} className="card-title">
            {room.name}
          </Link>
          <div className="card-meta">
            <span className={`status status-${room.status.toLowerCase()}`}>{room.status}</span>
            <span className="chip">{room.memberRole}</span>
            <span className="muted small">{room.organizationName}</span>
            <span className="muted small">
              {room.documentCount} document{room.documentCount === 1 ? '' : 's'} ·{' '}
              {room.memberCount} member
              {room.memberCount === 1 ? '' : 's'}
            </span>
            {room.accessEndsUtc && (
              <span className="small muted">Access ends {formatUtc(room.accessEndsUtc)}</span>
            )}
          </div>
        </li>
      ))}
    </ul>
  )
}

const roomSchema = z.object({
  organizationId: z.string().min(1, 'Pick an organization'),
  name: z.string().trim().min(1, 'A name is required').max(200),
  description: z.string().trim().max(2000).optional(),
})
type RoomForm = z.infer<typeof roomSchema>

function CreateRoomPanel({ organizations }: { organizations: { id: string; name: string }[] }) {
  const create = useCreateRoom()
  const form = useForm<RoomForm>({
    resolver: zodResolver(roomSchema),
    defaultValues: { organizationId: organizations[0]?.id ?? '', name: '', description: '' },
  })
  const serverErrors = create.error instanceof ApiError ? create.error.violations : {}

  return (
    <div className="panel">
      <h2 className="h2">New room</h2>
      <form
        className="form"
        onSubmit={form.handleSubmit((values) =>
          create.mutate(
            {
              organizationId: values.organizationId,
              name: values.name,
              description: values.description || null,
            },
            {
              onSuccess: () =>
                form.reset({ organizationId: values.organizationId, name: '', description: '' }),
            },
          ),
        )}
      >
        <label className="field">
          <span>Organization</span>
          <select {...form.register('organizationId')}>
            {organizations.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Name</span>
          <input {...form.register('name')} autoComplete="off" />
          <span className="field-error">
            {form.formState.errors.name?.message ?? serverErrors.name}
          </span>
        </label>
        <label className="field">
          <span>Description</span>
          <input {...form.register('description')} autoComplete="off" />
        </label>
        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={create.isPending}>
            Create room
          </button>
          {create.isError && !serverErrors.name && (
            <span className="error-text small">{errorMessage(create.error)}</span>
          )}
          {create.isSuccess && (
            <span className="small muted">
              Created. It starts in Draft; open it when it is ready.
            </span>
          )}
        </div>
      </form>
    </div>
  )
}

const orgSchema = z.object({
  name: z.string().trim().min(1, 'A name is required').max(200),
  slug: z
    .string()
    .trim()
    .min(2)
    .max(64)
    .regex(/^[a-z0-9-]+$/, 'Lowercase letters, digits and hyphens'),
})
type OrgForm = z.infer<typeof orgSchema>

function CreateOrganizationPanel() {
  const create = useCreateOrganization()
  const [open, setOpen] = useState(false)
  const form = useForm<OrgForm>({
    resolver: zodResolver(orgSchema),
    defaultValues: { name: '', slug: '' },
  })
  const serverErrors = create.error instanceof ApiError ? create.error.violations : {}

  if (!open) {
    return (
      <p className="small muted">
        Platform administration:{' '}
        <button type="button" className="link-btn" onClick={() => setOpen(true)}>
          create an organization
        </button>
      </p>
    )
  }

  return (
    <div className="panel">
      <h2 className="h2">New organization</h2>
      <p className="small muted">You become its first owner, and can then create rooms in it.</p>
      <form
        className="form"
        onSubmit={form.handleSubmit((values) =>
          create.mutate(values, { onSuccess: () => form.reset() }),
        )}
      >
        <label className="field">
          <span>Name</span>
          <input {...form.register('name')} autoComplete="off" />
          <span className="field-error">
            {form.formState.errors.name?.message ?? serverErrors.name}
          </span>
        </label>
        <label className="field">
          <span>Slug</span>
          <input {...form.register('slug')} autoComplete="off" className="mono" />
          <span className="field-error">
            {form.formState.errors.slug?.message ?? serverErrors.slug}
          </span>
        </label>
        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={create.isPending}>
            Create organization
          </button>
          <button type="button" className="btn" onClick={() => setOpen(false)}>
            Close
          </button>
          {create.isSuccess && <span className="small muted">Created.</span>}
        </div>
      </form>
    </div>
  )
}
